import { useArchiveViewStore } from '../store/archiveViewStore.js'

/**
 * withArchiveGuard — wrap a TanStack Query mutation result so that its
 * `mutate` / `mutateAsync` calls silently no-op when the dashboard is
 * rendering a frozen archive week.
 *
 * The bug we're preventing: user clicks into a past week via the
 * Weekly Archive panel. The dashboard swaps its data source to the
 * snapshot via the archiveViewStore. Read-only UI wrappers (EditableValue,
 * WeeklySummary, TeamUpdateCard, ExecutiveSummaryCard) correctly
 * disable their click handlers. But any component that DIDN'T get
 * plumbed with the `useIsReadOnly` check — OwnerPicker, ActionItems,
 * CommentPanel, DmPanel, SupportPicker, FollowUpsSection, and
 * automatic writers like useGscGoalSync — would happily fire mutations
 * that hit the live KV store. Users would think they were editing the
 * archive; they'd actually be corrupting the live week's data.
 *
 * Wrapping the mutation result centralizes the guard: every hook that
 * uses withArchiveGuard refuses to dispatch writes while active,
 * regardless of which UI path called it. Cleaner than chasing down
 * every component.
 *
 * Implementation note: we read the store via getState() rather than
 * subscribing. This is an event-handler path, not a render path — we
 * want the check to reflect the current moment, not cause re-renders.
 */
export function withArchiveGuard(mutation) {
  const isBlocked = () => useArchiveViewStore.getState().active

  return {
    ...mutation,
    mutate: (vars, opts) => {
      if (isBlocked()) {
        console.warn('[archive-view] mutation blocked while viewing a past week')
        return undefined
      }
      return mutation.mutate(vars, opts)
    },
    mutateAsync: (vars, opts) => {
      if (isBlocked()) {
        console.warn('[archive-view] mutation blocked while viewing a past week')
        return Promise.reject(new Error('Dashboard is in read-only archive view'))
      }
      return mutation.mutateAsync(vars, opts)
    },
  }
}
